// @ts-expect-error - blueimp-md5 has no type declarations
import md5 from 'blueimp-md5';
import { useCallback, useEffect, useState } from 'react';
import { getRegistrationSteps } from '../../constants/registration';
import { useApiRequest } from '../../hooks/useApiRequest';
import useDataLayer from '../../hooks/useDataLayer';
import useForm from '../../hooks/useForm';
import useIsExistingCompany from '../../hooks/useIsExistingCompany';
import {
  selectedCompany,
  type UserRegistrationFields,
} from '../../types/registration';
import FormProgress from '../formProgress';
import InstructionalForm from '../instructionalForm';
import CompanyDetails from './companyDetails';
import Confirmation from './confirmation';
import ExistingServiceDetails from './existingServiceDetails';
import NewServiceDetails from './newServiceDetails';
import UserDetails from './userDetails';

const Registration = ({ formStep = 1 }) => {
  const initialFields: UserRegistrationFields = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    mobileNumber: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    marketingOptions: [],
    companyName: '',
    address1: '',
    address2: '',
    town: '',
    county: '',
    postcode: '',
    companyType: '',
    services: [
      {
        title: '',
        position: 1,
        isOpen: true,
        serviceType: '',
        service: '',
        serviceIdentifier: '',
        isCommissionedService: '',
        serviceCommissioningBody: '',
        isLicensedForService: '',
        serviceLicensingBody: '',
        serviceRegistrationNo: '',
      },
    ],
    existingClientServices: [],
    agreePrivacy: '',
    agreeTerms: '',
  };

  const { handleChange, resetField, fields } = useForm(initialFields);

  const [currentStep, setCurrentStep] = useState(formStep);

  const [submissionErrors, setSubmissionErrors] = useState([]);

  const apiRequest = useApiRequest();

  const { isExistingCompany } = useIsExistingCompany(fields);

  const buildUserRegistration = useCallback(() => {
    const company = selectedCompany(fields.companyName);

    const services = isExistingCompany()
      ? fields.existingClientServices.map(serviceId => ({ serviceId }))
      : fields.services.map(service => ({
          serviceType: service.serviceType,
          serviceId: service.service,
          serviceIdentifier: service.serviceIdentifier,
          isCommissionedService: service.isCommissionedService === 'Yes',
          serviceCommissioningBody: service.serviceCommissioningBody,
          isLicensedForService: service.isLicensedForService === 'Yes',
          serviceLicensingBody: service.serviceLicensingBody,
          serviceRegistrationNo: service.serviceRegistrationNo,
        }));

    const userRegistration = {
      firstName: fields.firstName,
      lastName: fields.lastName,
      phoneNumber: fields.phoneNumber,
      mobileNumber: fields.mobileNumber,
      email: fields.email,
      confirmEmail: fields.confirmEmail,
      password: fields.password,
      confirmPassword: fields.confirmPassword,
      marketingOptions: fields.marketingOptions,
      client: {
        name: company?.label,
        id: company?.value,
      },
      existingClient: isExistingCompany(),
      address: {
        // eslint-disable-next-line camelcase
        address_1: fields.address1,
        // eslint-disable-next-line camelcase
        address_2: fields.address2,
        town: fields.town,
        county: fields.county,
        postcode: fields.postcode,
      },
      companyType: fields.companyType,
      services,
      agreePrivacy: fields.agreePrivacy,
      agreeTerms: fields.agreeTerms,
    };

    return { userRegistration };
  }, [fields, isExistingCompany]);

  const pushDataLayer = useDataLayer();
  const pushDataLayerStep = useCallback(
    (step: number) => {
      pushDataLayer({
        event: 'ospace.registration',
        step,
        isExistingClient: step >= 2 ? isExistingCompany() : null,
        isComplete: step === 4,
        hashedEmail: md5(fields.email),
      });
    },
    [pushDataLayer, fields.email, isExistingCompany]
  );

  const submitUserRegistration = useCallback(() => {
    apiRequest
      .post('/v1/users/registrations', buildUserRegistration())
      .then(() => {
        pushDataLayerStep(4);
        setCurrentStep(4);
      })
      .catch(error => {
        setSubmissionErrors(error.response.data.messages);
      });
  }, [apiRequest, buildUserRegistration, pushDataLayerStep]);

  const proceedToStep = (step: number) => {
    pushDataLayerStep(step - 1);
    setCurrentStep(step);
  };

  const renderForm = (step: number) => {
    switch (step) {
      case 1: {
        return (
          <UserDetails
            fields={fields}
            handleFieldChange={handleChange}
            handleSubmitCallback={() => proceedToStep(2)}
          />
        );
      }

      case 2: {
        return (
          <CompanyDetails
            fields={fields}
            existingCompany={isExistingCompany()}
            handleFieldChange={handleChange}
            handleSubmitCallback={() => proceedToStep(3)}
            handleGoBack={() => setCurrentStep(1)}
          />
        );
      }

      case 3: {
        if (isExistingCompany()) {
          return (
            <ExistingServiceDetails
              fields={fields}
              submissionErrors={submissionErrors}
              handleFieldChange={handleChange}
              handleSubmitCallback={() => submitUserRegistration()}
              handleGoBack={() => proceedToStep(2)}
            />
          );
        }
        return (
          <NewServiceDetails
            fields={fields}
            submissionErrors={submissionErrors}
            handleFieldChange={handleChange}
            handleResetField={resetField}
            handleSubmitCallback={() => submitUserRegistration()}
            handleGoBack={() => proceedToStep(2)}
          />
        );
      }

      default: {
        return <></>;
      }
    }
  };

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  const registrationSteps = getRegistrationSteps();

  return (
    <div className="registration-form">
      <FormProgress steps={registrationSteps} currentStep={currentStep} />

      {currentStep < 4 && (
        <InstructionalForm
          form={renderForm(currentStep)}
          instructions={registrationSteps[currentStep - 1].content}
        />
      )}

      {currentStep === 4 && (
        <Confirmation
          email={fields.email}
          existingCompany={isExistingCompany()}
        />
      )}
    </div>
  );
};

export default Registration;
